import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";

// ─── Slide Data ──────────────────────────────────────────────────────────────

const buildSlides = (labName) => [
  // SLIDE 1 — AI Insights
  {
    id: "ai-insights",
    gradient: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #1d4ed8 100%)",
    accentColor: "#60a5fa",
    tag: `✦ AI-POWERED  ·  EXCLUSIVE FOR ${labName || "YOUR LAB"}`,
    tagColor: "rgba(96,165,250,0.18)",
    tagBorder: "rgba(96,165,250,0.35)",
    headline: "Boost your lab's growth with",
    headlineHighlight: "Intelligent AI Insights",
    body:
      "Uncover what's driving your top packages, predict subscription risks before they happen, and get personalised recommendations to scale your diagnostic operations — powered by NirogGyan AI.",
    pills: [
      { icon: "⚡", label: "Usage Anomaly Detection" },
      { icon: "📦", label: "Package Growth Signals" },
      { icon: "🔔", label: "Renewal Risk Alerts" },
      { icon: "📊", label: "Lab Performance Score" },
    ],
    stats: [
      { value: "AI", label: "Powered Insights" },
      { value: "Live", label: "Usage Tracking" },
      { value: "Smart", label: "Recommendations" },
    ],
    cta: { label: "Know More", isNew: true, action: "coming-soon" },
    layout: "ai",
  },
  // SLIDE 2 — Enterprise Upgrade
  {
    id: "enterprise",
    gradient: "linear-gradient(135deg, #0c4a6e 0%, #0369a1 45%, #0891b2 100%)",
    accentColor: "#38bdf8",
    tag: "🚀  ENTERPRISE PLAN",
    tagColor: "rgba(56,189,248,0.18)",
    tagBorder: "rgba(56,189,248,0.35)",
    headline: "Unlock Advanced",
    headlineHighlight: "AI Features",
    body:
      "Upgrade to our Enterprise plan and get 50% more processing power, custom workflows, and priority support — built for labs that demand the best.",
    pills: [
      { icon: "⚙️", label: "Custom Workflows" },
      { icon: "🛡️", label: "Priority Support" },
      { icon: "⚡", label: "50% More Power" },
      { icon: "🤖", label: "Advanced AI Models" },
    ],
    stats: [
      { value: "50%", label: "More Power" },
      { value: "24/7", label: "Priority Support" },
      { value: "∞", label: "Custom Workflows" },
    ],
    cta: { label: "Upgrade Now", isNew: false, action: "coming-soon" },
    layout: "enterprise",
  },
  // SLIDE 3 — Blog Post
  {
    id: "blog",
    gradient: "linear-gradient(135deg, #1e1b4b 0%, #312e81 45%, #4338ca 100%)",
    accentColor: "#a78bfa",
    tag: "LATEST FROM OUR BLOG",
    tagColor: "rgba(167,139,250,0.18)",
    tagBorder: "rgba(167,139,250,0.35)",
    headline: "Stay Updated with the Latest in",
    headlineHighlight: "Diagnostic Innovation",
    body:
      "Explore expert insights, product updates, and real-world impact stories on how automation and smart reporting are transforming diagnostics for labs, doctors, and patients — powered by NirogGyan.",
    pills: [
      {
        icon: (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ),
        label: "Faster Workflows",
      },
      {
        icon: (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ),
        label: "Reduced Errors",
      },
      {
        icon: (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path d="M18 20V10M12 20V4M6 20v-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ),
        label: "Actionable Insights",
      },
      {
        icon: (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ),
        label: "Seamless Integration",
      },
    ],
    stats: [],
    cta: { label: "Explore All Blogs", isNew: false, action: "open-link", url: "https://www.niroggyan.com/blogs/" },
    layout: "blog",
  },
  // SLIDE 4 — Roadmap Teaser
  {
    id: "roadmap",
    gradient: "linear-gradient(135deg, #064e3b 0%, #065f46 45%, #0d9488 100%)",
    accentColor: "#34d399",
    tag: "🔮  COMING SOON  ·  NIROGGYAN ROADMAP",
    tagColor: "rgba(52,211,153,0.18)",
    tagBorder: "rgba(52,211,153,0.35)",
    headline: "The Future of Diagnostic",
    headlineHighlight: "Intelligence Is Here",
    body:
      "From Personalised Diet Recommendations and Test Suggestions to Path to Wellness Reports — we're building the next generation of health intelligence for your lab.",
    pills: [
      { icon: "🥗", label: "Diet Recommendations" },
      { icon: "🧪", label: "Test Recommendations" },
      { icon: "📍", label: "Path to Wellness" },
      { icon: "🧬", label: "Microbiology Reports" },
    ],
    stats: [
      { value: "4+", label: "Upcoming Features" },
      { value: "Q2", label: "Expected Launch" },
      { value: "Labs", label: "Benefiting" },
    ],
    cta: { label: "Know More", isNew: false, action: "coming-soon" },
    layout: "roadmap",
  },
];

// ─── Pill Chip ────────────────────────────────────────────────────────────────
const PillChip = ({ icon, label, accentColor }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "5px",
      background: "rgba(255,255,255,0.1)",
      border: "1px solid rgba(255,255,255,0.18)",
      borderRadius: "999px",
      px: "10px",
      py: "4px",
      backdropFilter: "blur(4px)",
      transition: "all 0.2s ease",
      "&:hover": {
        background: "rgba(255,255,255,0.16)",
        transform: "translateY(-1px)",
      },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", color: "rgba(255,255,255,0.9)", lineHeight: 1, flexShrink: 0 }}>{icon}</Box>
    <Typography
      sx={{
        fontSize: "11px",
        fontWeight: 600,
        color: "rgba(255,255,255,0.9)",
        letterSpacing: "0.2px",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
  </Box>
);

// ─── Stat Block ───────────────────────────────────────────────────────────────
const StatBlock = ({ value, label, accentColor }) => (
  <Box
    sx={{
      textAlign: "center",
      background: "rgba(255,255,255,0.08)",
      borderRadius: "12px",
      px: "18px",
      py: "10px",
      border: "1px solid rgba(255,255,255,0.12)",
      backdropFilter: "blur(8px)",
      minWidth: 72,
    }}
  >
    <Typography
      sx={{
        fontSize: { xs: "18px", md: "22px" },
        fontWeight: 800,
        color: "#fff",
        lineHeight: 1,
        letterSpacing: "-0.5px",
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: "10px",
        color: "rgba(255,255,255,0.6)",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        mt: 0.4,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
  </Box>
);

// ─── CTA Button ───────────────────────────────────────────────────────────────
const CtaButton = ({ label, isNew, onClick }) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      background: "#fff",
      color: "#1e3a8a",
      border: "none",
      borderRadius: "12px",
      px: "22px",
      py: "11px",
      cursor: "pointer",
      fontWeight: 800,
      fontSize: "13px",
      letterSpacing: "0.2px",
      boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
      transition: "all 0.25s cubic-bezier(0.34,1.56,0.64,1)",
      "&:hover": {
        transform: "translateY(-2px) scale(1.03)",
        boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
      },
      "&:active": { transform: "translateY(0) scale(0.98)" },
    }}
  >
    {label} →
    {isNew && (
      <Box
        sx={{
          background: "linear-gradient(135deg, #f97316, #dc2626)",
          color: "#fff",
          fontSize: "9px",
          fontWeight: 800,
          borderRadius: "5px",
          px: "6px",
          py: "2px",
          letterSpacing: "0.8px",
          lineHeight: 1.4,
        }}
      >
        NEW
      </Box>
    )}
  </Box>
);

// ─── Main Carousel ────────────────────────────────────────────────────────────
const DashboardCarousel = ({ loginData }) => {
  const labName = loginData?.labName || loginData?.org || "";
  const slides = buildSlides(labName);

  const [active, setActive] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState("right"); // 'right' or 'left'
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const timerRef = useRef(null);

  const goTo = useCallback(
    (index, dir = "right") => {
      if (animating) return;
      setDirection(dir);
      setAnimating(true);
      setTimeout(() => {
        setActive(index);
        setAnimating(false);
      }, 380);
    },
    [animating]
  );

  const next = useCallback(() => {
    goTo((active + 1) % slides.length, "right");
  }, [active, goTo, slides.length]);

  const prev = useCallback(() => {
    goTo((active - 1 + slides.length) % slides.length, "left");
  }, [active, goTo, slides.length]);

  // Auto-advance
  useEffect(() => {
    if (hovered) return;
    timerRef.current = setInterval(next, 5500);
    return () => clearInterval(timerRef.current);
  }, [hovered, next]);

  const handleCta = (action, url) => {
    if (action === "coming-soon") setComingSoonOpen(true);
    if (action === "open-link" && url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const slide = slides[active];

  return (
    <>
      {/* ── Coming Soon Dialog ── */}
      <Dialog
        open={comingSoonOpen}
        onClose={() => setComingSoonOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            p: 1,
            maxWidth: 400,
            width: "90%",
            boxShadow: "0 24px 80px rgba(0,0,0,0.18)",
          },
        }}
      >
        <IconButton
          onClick={() => setComingSoonOpen(false)}
          sx={{ position: "absolute", top: 10, right: 10, color: "#9DA3AE" }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <DialogContent sx={{ textAlign: "center", pt: 4, pb: 1 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "16px",
              background: "linear-gradient(135deg, #3567D6 0%, #170C6B 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2.5,
              boxShadow: "0 8px 24px rgba(53,103,214,0.3)",
            }}
          >
            <RocketLaunchIcon sx={{ color: "#fff", fontSize: 30 }} />
          </Box>
          <Typography
            sx={{ fontWeight: 800, fontSize: "20px", color: "#170C6B", mb: 1.5 }}
          >
            Coming Soon! 🚀
          </Typography>
          <Typography
            sx={{
              fontSize: "13.5px",
              color: "#6B7280",
              lineHeight: 1.6,
              maxWidth: 300,
              mx: "auto",
            }}
          >
            Our sales team will reach out to you shortly with a personalised
            Enterprise plan upgrade offer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3.5, pb: 3, justifyContent: "center", gap: 1.5 }}>
          <Button
            onClick={() => setComingSoonOpen(false)}
            sx={{
              borderRadius: "12px",
              border: "1.5px solid #D0DEFF",
              background: "#fff",
              color: "#3567D6",
              fontWeight: 600,
              px: 3,
              "&:hover": { background: "#f0f4ff" },
            }}
          >
            Close
          </Button>
          <Button
            onClick={() => setComingSoonOpen(false)}
            sx={{
              borderRadius: "12px",
              background: "linear-gradient(135deg, #3567D6, #170C6B)",
              color: "#fff",
              fontWeight: 700,
              px: 3,
              boxShadow: "0 4px 16px rgba(53,103,214,0.35)",
              "&:hover": { opacity: 0.9 },
            }}
          >
            OK, Got It
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Carousel Wrapper (Arrows Outside) ── */}
      <Box
        sx={{ position: "relative", userSelect: "none" }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* ── Carousel Shell (Background & Slide Content) ── */}
        <Box
          sx={{ borderRadius: "20px", overflow: "hidden", position: "relative" }}
        >
        {/* Slide Track */}
        <Box
          sx={{
            background: slide.gradient,
            minHeight: { xs: 240, sm: 220, md: 200 },
            position: "relative",
            px: { xs: 3, md: 5 },
            py: { xs: 3, md: 3.5 },
            transition: "background 0.6s ease",
            overflow: "hidden",
          }}
        >
          {/* Decorative floating orbs */}
          <Box
            sx={{
              position: "absolute",
              top: -60,
              right: -60,
              width: 280,
              height: 280,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.04)",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -40,
              right: "30%",
              width: 160,
              height: 160,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.03)",
              pointerEvents: "none",
            }}
          />

          {/* Slide Content — animated */}
          <Box
            sx={{
              opacity: animating ? 0 : 1,
              transform: animating
                ? direction === "right"
                  ? "translateX(24px)"
                  : "translateX(-24px)"
                : "translateX(0)",
              transition: "opacity 0.38s ease, transform 0.38s ease",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ xs: "flex-start", md: "center" }}
              gap={{ xs: 2.5, md: 4 }}
            >
              {/* ── LEFT: Text block ── */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Tag */}
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    background: slide.tagColor,
                    border: `1px solid ${slide.tagBorder}`,
                    borderRadius: "999px",
                    px: "12px",
                    py: "4px",
                    mb: 1.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "10px",
                      fontWeight: 700,
                      color: slide.accentColor,
                      letterSpacing: "0.8px",
                      textTransform: "uppercase",
                    }}
                  >
                    {slide.tag}
                  </Typography>
                </Box>

                {/* Headline */}
                <Typography
                  sx={{
                    fontSize: { xs: "20px", md: "24px" },
                    fontWeight: 800,
                    color: "#fff",
                    lineHeight: 1.2,
                    mb: 0.5,
                  }}
                >
                  {slide.headline}{" "}
                  <Box
                    component="span"
                    sx={{
                      background: `linear-gradient(90deg, ${slide.accentColor}, #fff)`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      display: "inline",
                    }}
                  >
                    {slide.headlineHighlight}
                  </Box>
                </Typography>

                {/* Body */}
                <Typography
                  sx={{
                    fontSize: { xs: "12px", md: "12.5px" },
                    color: "rgba(255,255,255,0.72)",
                    lineHeight: 1.65,
                    mb: 2,
                    maxWidth: 520,
                  }}
                >
                  {slide.body}
                </Typography>

                {/* Pills */}
                <Stack direction="row" flexWrap="wrap" gap={1} mb={2.5}>
                  {slide.pills.map((p) => (
                    <PillChip key={p.label} icon={p.icon} label={p.label} accentColor={slide.accentColor} />
                  ))}
                </Stack>

                {/* CTA */}
                <CtaButton
                  label={slide.cta.label}
                  isNew={slide.cta.isNew}
                  onClick={() => handleCta(slide.cta.action, slide.cta.url)}
                />
              </Box>

              {/* ── RIGHT: Stats block (only if there are stats) ── */}
              {slide.stats && slide.stats.length > 0 && (
                <Stack
                  direction={{ xs: "row", md: "column" }}
                  gap={1.25}
                  sx={{ flexShrink: 0 }}
                >
                  {slide.stats.map((s) => (
                    <StatBlock key={s.label} value={s.value} label={s.label} accentColor={slide.accentColor} />
                  ))}
                </Stack>
              )}
            </Stack>
          </Box>
        </Box>

        {/* ── Progress Bar ── */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "rgba(255,255,255,0.12)",
          }}
        >
          <Box
            key={`${active}-${hovered}`}
            sx={{
              height: "100%",
              background: `linear-gradient(90deg, ${slide.accentColor}, #fff)`,
              borderRadius: "0 2px 2px 0",
              animation: hovered ? "none" : `carouselProgress 5.5s linear forwards`,
              "@keyframes carouselProgress": {
                "0%": { width: "0%" },
                "100%": { width: "100%" },
              },
            }}
          />
        </Box>

        {/* ── Dot Indicators ── */}
        <Stack
          direction="row"
          gap={0.75}
          sx={{
            position: "absolute",
            bottom: 14,
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          {slides.map((_, i) => (
            <Box
              key={i}
              onClick={() => goTo(i, i > active ? "right" : "left")}
              sx={{
                width: i === active ? 22 : 7,
                height: 7,
                borderRadius: "999px",
                background: i === active ? "#fff" : "rgba(255,255,255,0.35)",
                cursor: "pointer",
                transition: "all 0.35s cubic-bezier(0.34,1.56,0.64,1)",
                "&:hover": { background: "rgba(255,255,255,0.7)" },
              }}
            />
          ))}
        </Stack>
        </Box>

        {/* ── Left Arrow ── */}
        <IconButton
          onClick={prev}
          size="small"
          sx={{
            position: "absolute",
            left: { xs: -12, md: -18 },
            top: "50%",
            transform: "translateY(-50%)",
            background: "#fff",
            border: "1px solid #E8EEF8",
            color: "#3567D6",
            width: 38,
            height: 38,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            zIndex: 10,
            transition: "all 0.2s ease",
            "&:hover": { background: "#f8faff", transform: "translateY(-50%) scale(1.1)" },
          }}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>

        {/* ── Right Arrow ── */}
        <IconButton
          onClick={next}
          size="small"
          sx={{
            position: "absolute",
            right: { xs: -12, md: -18 },
            top: "50%",
            transform: "translateY(-50%)",
            background: "#fff",
            border: "1px solid #E8EEF8",
            color: "#3567D6",
            width: 38,
            height: 38,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            zIndex: 10,
            transition: "all 0.2s ease",
            "&:hover": { background: "#f8faff", transform: "translateY(-50%) scale(1.1)" },
          }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>

      </Box>
    </>
  );
};

export default DashboardCarousel;
